import type { InternalLayer } from 'hooks/internalLayer/types';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { LayerFeaturesContent } from './LayerFeaturesContent';
import { LayerMetadata } from './LayerDataRows';
import { Layer } from './Layer';
import { InternalLayerVersionSelect } from './InternalLayerVersionSelect';
import { DraftNewVersionButton } from './DraftNewVersionButton';
import { useListInternalLayerVersionsQuery } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import { EditInternalLayerVersionButton } from './EditInternalLayerVersionButton';
import { useUscgMapContext } from './UscgMapContext';
import { useMemo } from 'react';
import { useDeleteLocation } from 'hooks/icsLocation/useDeleteLocation';

function InternalLayerListItemContent({ layer }: { layer: InternalLayer }) {
  const { getSelectedVersionForLayer } = useUscgMapContext();
  const activeVersionId = getSelectedVersionForLayer(layer.id)?.id;

  const deleteLocation = useDeleteLocation();

  const handleDeleteLocation = async (objectId: string) => {
    await deleteLocation.mutateAsync(objectId);
  };

  const { data } = useListInternalLayerVersionsQuery({
    filter: {
      internalLayerId: layer.id,
    },
  });
  const versions = useMemo(() => data?.data ?? [], [data?.data]);

  const activeVersion = versions.find((v) => v.id === activeVersionId);
  const canEditVersion = activeVersion?.approval?.approvalStatus === 'DRAFT';

  return (
    <>
      <DraftNewVersionButton internalLayer={layer} />
      {canEditVersion && (
        <EditInternalLayerVersionButton internalLayer={layer} />
      )}
      <InternalLayerVersionSelect layer={layer} />
      <LayerMetadata
        lastUpdated={formatDateTime(layer.updatedAt)}
        aorIds={layer.aorIds}
        incident={layer.incidentId}
        sources="PRATUS"
      />
      <LayerFeaturesContent
        layer={layer}
        layerType="internal"
        showAddObjectButton={false}
        // not yet implemented
        // onEdit={canEditVersion ? handleEditLocation : undefined}
        onDelete={canEditVersion ? handleDeleteLocation : undefined}
      />
    </>
  );
}

type InternalLayerItemProps = {
  layer: InternalLayer;
  onDelete: () => void;
  onOpenInNewClick?: () => void;
  onZoomToLayer?: () => void;
};

export function InternalLayerItem({
  layer,
  onOpenInNewClick,
  onDelete,
  onZoomToLayer,
}: InternalLayerItemProps) {
  return (
    <Layer.Item
      eventKey={`internal-${layer.id}`}
      header={
        <Layer.Header
          title={layer.name}
          layerId={layer.id}
          layerType="internal"
          onOpenInNew={onOpenInNewClick}
          onZoomToLayer={onZoomToLayer}
          onDelete={onDelete}
        />
      }
    >
      <Layer.Content>
        <InternalLayerListItemContent layer={layer} />
      </Layer.Content>
    </Layer.Item>
  );
}
